import type { useRender } from "@base-ui/react/use-render";
import { Card } from "@bednbody/ui/components/card";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { cn } from "@bednbody/ui/lib/utils";
import type { Icon } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

const iconVariants = cva("flex size-10 shrink-0 items-center justify-center rounded-xl", {
  variants: {
    tone: {
      default: "bg-primary/10 text-primary-dark dark:text-primary-light",
      success: "bg-success/15 text-success",
      warning: "bg-warning/15 text-warning",
      danger: "bg-destructive/15 text-destructive",
    },
  },
  defaultVariants: { tone: "default" },
});

type TileTone = NonNullable<VariantProps<typeof iconVariants>["tone"]>;

/**
 * A pressed tile wears its own tone on BOTH edges, so "Unassigned is selected"
 * and "Urgent is selected" do not both come out teal. `highlight` cannot do
 * this: it is hardcoded primary, which is right for a read-only tile that just
 * wants emphasis, and wrong for a filter whose whole identity is its tone.
 */
const SELECTED_BORDER: Record<TileTone, string> = {
  default: "border-primary/40",
  success: "border-success/40",
  warning: "border-warning/40",
  danger: "border-destructive/40",
};

const SELECTED_FILL: Record<TileTone, string> = {
  default: "border-primary/25 bg-primary/10",
  success: "border-success/25 bg-success/10",
  warning: "border-warning/25 bg-warning/10",
  danger: "border-destructive/25 bg-destructive/10",
};

const SELECTED_ICON: Record<TileTone, string> = {
  default: "text-primary",
  success: "text-success",
  warning: "text-warning",
  danger: "text-destructive",
};

export interface StatTileProps
  extends VariantProps<typeof iconVariants>,
    useRender.ComponentProps<"div"> {
  /**
   * `compact` stacks label/icon above value/context. `row` is the same
   * information as one slim band instead — for a count wide enough to span a
   * grid row without pretending to be another square.
   */
  layout?: "default" | "compact" | "row";
  label?: ReactNode;
  detail?: ReactNode;
  /** Read-only emphasis. Primary only, and not a pressed state. */
  highlight?: boolean;
  /**
   * The pressed state of a filter tile: tone-aware border and fill, plus
   * `aria-pressed`, because a tile in a set of filters IS a toggle. Only
   * meaningful alongside `render` — a tile that is not a control has no
   * pressed state, and `highlight` is what it wants.
   */
  selected?: boolean;
  icon: Icon;
  children: ReactNode;
  adornment?: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function StatTile({
  icon: IconComponent,
  children,
  adornment,
  footer,
  tone,
  className,
  layout = "default",
  label,
  detail,
  highlight = false,
  selected = false,
  render,
  ...props
}: StatTileProps) {
  const key = (tone ?? "default") as TileTone;
  // The PRESENCE of `render` is the switch, so no caller can pass an onClick
  // and forget the hover/focus treatment. OptionCard does the same check.
  const control = render !== undefined;

  const iconClass = cn(
    "size-6 shrink-0",
    selected
      ? SELECTED_ICON[key]
      : key === "danger"
        ? "text-destructive"
        : key === "warning"
          ? "text-warning"
          : key === "success"
            ? "text-success"
            : highlight
              ? "text-primary"
              : "text-muted-foreground",
  );

  // The ring goes on the OUTER element, because that is the one that becomes
  // the button. SurfaceCard's own `interactive` ring cannot do this job — it
  // sits on the inner panel, which never receives focus.
  const shellClass = cn(
    "h-full min-w-0 gap-0 rounded-xl border bg-card p-1 shadow-xs ring-0",
    selected ? SELECTED_BORDER[key] : highlight ? "border-primary/30" : "border-border",
    // Tailwind sorts variant utilities after base ones, so a plain
    // `hover:border-primary/30` would beat a selected tone's border the moment
    // the pointer lands — a red "Urgent" tile turning teal on hover. Only the
    // unselected state gets the hover lift.
    control && !selected
      ? "cursor-pointer transition-colors hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      : control
        ? "cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        : null,
    className,
  );

  const panelClass = (row: boolean) =>
    cn(
      "relative flex h-full min-w-0 overflow-hidden rounded-lg p-4 shadow-none",
      row ? "items-center gap-2.5" : "flex-col gap-3",
      selected ? SELECTED_FILL[key] : highlight ? "bg-primary/5" : "bg-muted/40",
      // Card names its group `group/card`, and the inner panel is what lifts on
      // hover — lifting only the outer rim reads as a border change.
      control ? "group-hover/card:bg-muted/60" : null,
    );

  const glyph = <IconComponent aria-hidden className={iconClass} weight="duotone" />;
  const labelText =
    label != null ? (
      <p className="min-w-0 truncate text-xs font-semibold uppercase text-muted-foreground">{label}</p>
    ) : null;

  // Before the spread, so a caller can still override it deliberately.
  const controlProps = control ? { "aria-pressed": selected, ...props } : props;

  if (layout === "row") {
    return (
      <Card {...controlProps} className={shellClass} render={render}>
        <SurfaceCard className={panelClass(true)}>
          {glyph}
          {labelText}
          {detail != null ? (
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">· {detail}</span>
          ) : null}
          {adornment}
          <span className="min-w-0 text-xl font-semibold leading-none text-foreground tabular-nums">
            {children}
          </span>
        </SurfaceCard>
      </Card>
    );
  }

  if (layout === "compact") {
    return (
      <Card {...controlProps} className={shellClass} render={render}>
        <SurfaceCard className={panelClass(false)}>
          <div className="flex min-w-0 items-start justify-between gap-3">
            {labelText}
            <span className="ml-auto">{glyph}</span>
          </div>
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
            <div className="min-w-0 text-2xl font-semibold leading-none text-foreground tabular-nums">
              {children}
            </div>
            {detail != null ? (
              <div className="min-w-0 text-xs text-pretty text-muted-foreground">{detail}</div>
            ) : null}
            {adornment}
          </div>
          {footer != null ? <div className="text-xs text-muted-foreground">{footer}</div> : null}
        </SurfaceCard>
      </Card>
    );
  }

  return (
    <SurfaceCard
      className={cn(
        "relative flex min-w-0 flex-col justify-between gap-3 overflow-hidden p-4",
        // SurfaceCard sets no border-width, so the border colour in
        // SELECTED_FILL would never paint — the `border` has to be asked for.
        selected ? cn("border", SELECTED_FILL[key]) : null,
        className,
      )}
      render={render}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        {/* No call site used to pass a label here, so `default` silently
            DROPPED it — a tile that looked like it had a caption and had
            none. Rendered whenever given, so that can't happen again. */}
        {labelText}
        <div className={cn(iconVariants({ tone }), label == null && "ml-auto")}>
          <IconComponent className="size-5" weight="fill" />
        </div>
        {adornment}
      </div>
      <div className="relative z-10 min-w-0">{children}</div>
      {detail != null ? (
        <div className="relative z-10 min-w-0 text-xs text-muted-foreground">{detail}</div>
      ) : null}
      {footer ? <div className="relative z-10 text-xs text-muted-foreground">{footer}</div> : null}
    </SurfaceCard>
  );
}